/**
 * GA4 Measurement ID — canonical EvoHome Improvements property.
 *
 * Loads gtag.js UNCONDITIONALLY (Consent Mode v2 compliant). The Consent
 * Mode default installed in main.tsx sets `analytics_storage: denied` until
 * the user accepts, so GA4 sends GDPR-friendly cookieless pings that power
 * Google's behavioural modelling. Without the script tag loaded at all,
 * GA4 reports zero visitors — which is what we were seeing.
 *
 * Production source of truth is the hardcoded constant below. The Lovable
 * frontend bundle is built without custom VITE_* env injection, so
 * `import.meta.env.VITE_GA4_MEASUREMENT_ID` is only honoured if a future
 * self-hosted / CI build supplies it as a build-time override.
 *
 * NOTE: 431406080 is the GA4 *property number*, NOT a measurement ID.
 * The measurement ID for the web data stream is G-G4PB3895SX.
 */
import { isMarketingAudience } from "@/lib/marketingAudience";

export const CANONICAL_GA4_ID = "G-G4PB3895SX";

/** Google Ads account tag (base tag only). */
export const GOOGLE_ADS_ID = "AW-18498295787";

export const GA4_ID =
  (import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined) ||
  CANONICAL_GA4_ID;

/**
 * Hostnames allowed to send data into the PRODUCTION marketing analytics
 * properties (GA4, Clarity, GTM, Meta Pixel).
 *
 * Lovable previews, published *.lovable.app URLs, branch/deploy previews,
 * localhost and headless test runs all executed the same bundle and were
 * therefore sending page_view events into the live GA4 property, inflating
 * users/sessions with non-customer traffic. First-party Supabase analytics
 * is unaffected — it keeps recording every environment.
 */
const PRODUCTION_ANALYTICS_HOSTS = [
  "evohomeimprovements.co.uk",
  "www.evohomeimprovements.co.uk",
];

export function isProductionAnalyticsHost(): boolean {
  if (typeof window === "undefined") return false;
  // Headless / automated browsers (Playwright, Puppeteer, Selenium) must never
  // pollute the production marketing properties, even when they are driven
  // against the real production hostname during QA.
  try {
    if (navigator.webdriver === true) return false;
  } catch {
    /* navigator unavailable — fall through to hostname check */
  }
  return PRODUCTION_ANALYTICS_HOSTS.includes(window.location.hostname.toLowerCase());
}

let loaded = false;

export function loadGA4(measurementId?: string) {
  if (loaded || typeof window === "undefined") return;
  // Same rule for every marketing property: production host, public page,
  // not a staff session. See src/lib/marketingAudience.ts.
  if (!isMarketingAudience(isProductionAnalyticsHost())) return;
  loaded = true;

  const id = measurementId || GA4_ID;


  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = window.gtag || function () { window.dataLayer!.push(arguments); };

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);

  window.gtag("js", new Date());
  window.gtag("config", id, {
    // Disable automatic page_view — we fire it manually via ScrollToTop
    // to avoid duplicate page_views on SPA route changes
    send_page_view: false,
  });
  // Google Ads base tag shares the single gtag.js script (no second loader).
  // Consent Mode defaults from consent.ts (ad_storage denied in GB/EU until
  // accepted) govern it exactly as they govern GA4.
  window.gtag("config", GOOGLE_ADS_ID);

  // Fire initial page_view manually
  window.gtag("event", "page_view", {
    page_path: window.location.pathname,
    page_title: document.title,
    page_location: window.location.href,
  });
}

/**
 * Initialize deferred GA4 loading — loads after idle or first user interaction.
 */
export function initDeferredGA4(measurementId?: string) {
  if (typeof window === "undefined") return;

  const load = () => loadGA4(measurementId);

  if (window.requestIdleCallback) {
    window.requestIdleCallback(load, { timeout: 3000 });
  } else {
    setTimeout(load, 2000);
  }

  const events = ["click", "scroll", "keydown", "touchstart"];
  const onInteraction = () => {
    load();
    events.forEach((e) => window.removeEventListener(e, onInteraction));
  };
  events.forEach((e) => window.addEventListener(e, onInteraction, { once: true, passive: true }));
}

// ---------------------------------------------------------------------------
// Microsoft Clarity — heatmaps, session recordings, funnel analysis
// ---------------------------------------------------------------------------

let clarityLoaded = false;

/**
 * Microsoft Clarity Project ID — canonical EvoHome Improvements project.
 *
 * Production source of truth is the hardcoded constant below (same pattern as
 * GA4) because the Lovable frontend bundle is built without custom VITE_*
 * env injection. `import.meta.env.VITE_CLARITY_ID` is still honoured first
 * for any future self-hosted / CI build that supplies an override.
 */
export const CANONICAL_CLARITY_ID = "wp301lq22f";
export const CLARITY_ID =
  (import.meta.env.VITE_CLARITY_ID as string | undefined) ||
  CANONICAL_CLARITY_ID;

/**
 * Load Microsoft Clarity unconditionally. Clarity respects Consent Mode v2
 * cookie settings automatically; gating the script tag itself produced zero
 * analytics data.
 */
export function loadClarity() {
  if (clarityLoaded || typeof window === "undefined") return;
  if (!isMarketingAudience(isProductionAnalyticsHost())) return;
  const id = CLARITY_ID;
  if (!id) return;
  clarityLoaded = true;

  (function (c: Window, l: Document, a: "clarity", r: string, i: string) {
    c[a] = c[a] || function (...args: unknown[]) { (c[a]!.q = c[a]!.q || []).push(args); };
    const t = l.createElement(r) as HTMLScriptElement;
    t.async = true;
    t.src = "https://www.clarity.ms/tag/" + i;
    const y = l.getElementsByTagName(r)[0] as HTMLElement;
    y.parentNode!.insertBefore(t, y);
  })(window, document, "clarity", "script", id);
}

/**
 * Initialize deferred Clarity loading — loads after idle or first interaction.
 */
export function initDeferredClarity() {
  if (typeof window === "undefined") return;

  const load = () => loadClarity();

  if (window.requestIdleCallback) {
    window.requestIdleCallback(load, { timeout: 4000 });
  } else {
    setTimeout(load, 3000);
  }
}
